import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import "../styles/MagazinePreview.css";

export default function DailyNewsPreview() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const mag = state?.mag;
  console.log("DailyNewsPreview received mag:", mag);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [currentPage, setCurrentPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  // Fetch magazine preview images from API
  useEffect(() => {
    const fetchPreview = async () => {
      if (!mag?.news_id) return;

      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}daily_news_preview.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ news_id: mag.news_id }),
        });
        const data = await res.json();
        console.log("Daily News Preview Data:", data);
        if (data?.head?.code === 200) {
          const imgs = [
            data.body.coverimg,
            data.body.firstimg,
            data.body.secondimg,
            data.body.thirdimg,
            data.body.fourthimg,
          ].filter(Boolean); // Remove null or empty
          setPages(imgs);
        } else {
          setPages([]);
        }
      } catch (err) {
        console.error("Failed to fetch magazine preview:", err);
        setPages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [API_BASE_URL, mag?.news_id]);

  // Window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") nextPage();
      if (e.key === "ArrowLeft") prevPage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  const nextPage = () => {
    if (!isLoggedIn && currentPage === 0) {
      navigate("/login");
      return;
    }
    setCurrentPage((prev) => (prev + 1 < pages.length ? prev + 1 : prev));
  };

  const prevPage = () => setCurrentPage((prev) => (prev - 1 >= 0 ? prev - 1 : prev));

  if (!mag) {
    return (
      <div className="container text-center mt-5">
        <h3>No Magazine Selected</h3>
        <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container text-center mt-5">
        <h4>Loading preview...</h4>
      </div>
    );
  }

  if (!pages.length) {
    return (
      <div className="container text-center mt-5">
        <h4>No preview available</h4>
        <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  const getBaseWidth = () => {
    if (windowWidth < 576) return 90;
    if (windowWidth < 768) return 70;
    return 50;
  };

  const getImageWidth = () => `${getBaseWidth() * zoom}vw`;

  return (
    <div className="magazine-preview-container">
      {/* Header */}
      <div className="header d-flex justify-content-between align-items-center p-3 shadow-sm bg-glass">
        <div className="mag-title">{mag.title} — Preview</div>
        <div className="header-buttons d-flex gap-2 flex-wrap justify-content-end">
          <div className="header-btn-wrapper" title="Back">
            <button className="btn btn-dark d-flex align-items-center gap-1" onClick={() => navigate(-1)}>
              <span className="btn-text d-none d-md-inline">Back</span>
              <ArrowLeft size={18} className="d-inline d-md-none" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Viewer */}
      <div className="viewer-wrapper d-flex flex-column align-items-center justify-content-center py-4">
        <img
          src={pages[currentPage]}
          alt={`Page ${currentPage + 1}`}
          className="page-img shadow-lg rounded-4"
          style={{
            width: getImageWidth(),
            height: "auto",
            transition: "width 0.3s ease",
          }}
        />

        {currentPage > 0 && (
          <button className="nav-btn prev-fixed" onClick={prevPage}>
            <ArrowLeft size={28} />
          </button>
        )}
        {currentPage < pages.length - 1 && (
          <button className="nav-btn next-fixed" onClick={nextPage}>
            <ArrowRight size={28} />
          </button>
        )}

        <div className="floating-zoom-controls">
          <button className="btn btn-secondary" onClick={() => setZoom((z) => Math.min(2, z + 0.1))}>
            <ZoomIn size={20} />
          </button>
          <button className="btn btn-secondary" onClick={() => setZoom((z) => Math.max(1, z - 0.1))}>
            <ZoomOut size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
